import { apiFetch } from "@/api/fetch";

// A grid can show 100+ figures. Load a few at a time: each request checks auth,
// and a burst of them gets 429 "Authentication service is busy".
const MAX_CONCURRENT_LOADS = 6;
// Loaded figures stay in memory, so a dataset opens again without new requests,
// and a grid's thumbnails can all load before they scroll into view.
const MAX_CACHED_FIGURES = 1000;
const MAX_RETRIES = 4;
const MAX_RETRY_DELAY_MS = 10_000;
const RETRY_STATUSES = new Set([429, 503]);

let activeLoads = 0;
const waitingLoads: { path: string; start: () => void }[] = [];
const cachedFigures = new Map<string, string>();
const loadingFigures = new Map<string, Promise<string>>();
const figureListeners = new Map<string, Set<(url: string) => void>>();

/**
 * With `source`, the figure's path in the cell value, the browser keeps the image
 * for good: the image at a path does not change, and a new figure in the cell
 * has a new path.
 */
export const figurePath = (
  datasetId: string,
  rowId: string,
  columnId: string,
  figureIndex: number,
  size: "full" | "thumb",
  source?: string,
) => {
  const params = new URLSearchParams();
  if (size === "thumb") params.set("size", "thumb");
  if (source) params.set("src", source);
  const query = params.toString();
  return `/extractions/${encodeURIComponent(datasetId)}/rows/${encodeURIComponent(
    rowId,
  )}/cells/${encodeURIComponent(columnId)}/figures/${figureIndex}${query ? `?${query}` : ""}`;
};

export const cachedFigure = (path: string) => {
  const url = cachedFigures.get(path);
  if (url) {
    cachedFigures.delete(path);
    cachedFigures.set(path, url);
  }
  return url ?? null;
};

const cacheFigure = (path: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  cachedFigures.set(path, url);
  while (cachedFigures.size > MAX_CACHED_FIGURES) {
    const [oldest, oldUrl] = cachedFigures.entries().next().value as [string, string];
    cachedFigures.delete(oldest);
    URL.revokeObjectURL(oldUrl);
  }
  figureListeners.get(path)?.forEach((listener) => listener(url));
  return url;
};

/** Calls `listener` with the URL of the figure when a load of it ends; returns the unsubscribe. */
export const onFigureLoaded = (path: string, listener: (url: string) => void) => {
  const listeners = figureListeners.get(path) ?? new Set();
  listeners.add(listener);
  figureListeners.set(path, listeners);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) figureListeners.delete(path);
  };
};

const acquireLoadSlot = (
  path: string,
  signal: AbortSignal,
  priority: boolean,
): Promise<void> => {
  if (signal.aborted) return Promise.reject(signal.reason);
  if (activeLoads < MAX_CONCURRENT_LOADS) {
    activeLoads += 1;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const entry = {
      path,
      start: () => {
        signal.removeEventListener("abort", cancel);
        resolve();
      },
    };
    const cancel = () => {
      const index = waitingLoads.indexOf(entry);
      if (index >= 0) waitingLoads.splice(index, 1);
      reject(signal.reason);
    };
    if (priority) waitingLoads.unshift(entry);
    else waitingLoads.push(entry);
    signal.addEventListener("abort", cancel, { once: true });
  });
};

/** Give the slot to the next waiting load, or free it. */
const releaseLoadSlot = () => {
  const next = waitingLoads.shift();
  if (next) next.start();
  else activeLoads -= 1;
};

/** Move a waiting load of `path` to the front of the queue. */
const hurry = (path: string) => {
  const index = waitingLoads.findIndex((entry) => entry.path === path);
  if (index > 0) waitingLoads.unshift(...waitingLoads.splice(index, 1));
};

const retryDelayMs = (retryAfter: string | null, attempt: number): number => {
  const seconds = Number(retryAfter);
  const delay =
    Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 500 * 2 ** attempt;
  return Math.min(delay, MAX_RETRY_DELAY_MS) + Math.random() * 250;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const done = () => {
      signal.removeEventListener("abort", cancel);
      resolve();
    };
    const timer = setTimeout(done, ms);
    const cancel = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    signal.addEventListener("abort", cancel, { once: true });
  });

const fetchFigure = async (
  path: string,
  signal: AbortSignal,
  priority: boolean,
): Promise<string> => {
  for (let attempt = 0; ; attempt += 1) {
    await acquireLoadSlot(path, signal, priority);
    let delayMs = 0;
    try {
      const response = await apiFetch(path, { signal });
      if (response.ok) return cacheFigure(path, await response.blob());
      if (!RETRY_STATUSES.has(response.status) || attempt >= MAX_RETRIES) {
        throw new Error("Figure unavailable");
      }
      delayMs = retryDelayMs(response.headers.get("Retry-After"), attempt);
    } finally {
      releaseLoadSlot();
    }
    await sleep(delayMs, signal);
  }
};

/**
 * The object URL of a figure. A second caller shares the load that is already
 * waiting or running; `priority` moves it to the front of the queue, for the
 * figures on screen. The load stops when the signal of its first caller aborts.
 */
export const loadFigure = (
  path: string,
  signal: AbortSignal,
  priority = false,
): Promise<string> => {
  const cached = cachedFigure(path);
  if (cached) return Promise.resolve(cached);
  const loading = loadingFigures.get(path);
  if (loading) {
    if (priority) hurry(path);
    return loading;
  }
  const load = fetchFigure(path, signal, priority).finally(() => {
    if (loadingFigures.get(path) === load) loadingFigures.delete(path);
  });
  loadingFigures.set(path, load);
  return load;
};

/** Queue the figures in order, behind the ones on screen, until the signal aborts. */
export const preloadFigures = (paths: string[], signal: AbortSignal) => {
  for (const path of paths.slice(0, MAX_CACHED_FIGURES)) {
    if (signal.aborted) return;
    loadFigure(path, signal).catch(() => undefined);
  }
};

export const isAbort = (error: unknown) =>
  error instanceof DOMException && error.name === "AbortError";
